import type {
  DocumentData,
  DocumentSnapshot,
  Query,
  QuerySnapshot,
  DocumentReference,
} from 'firebase/firestore'
import { onSnapshot } from 'firebase/firestore'
import { type Ref, ref } from 'vue'

import { absent, failed, loaded, loading, type LoadState } from '@/types/load-state'

export interface Subscription<T> {
  state: Ref<LoadState<T>>
  stop: () => void
}

/** Subscribes to one Firestore doc as a reactive `LoadState`; `mapper` returns `undefined` when absent. */
export function subscribeDoc<T>(
  docRef: DocumentReference<DocumentData>,
  mapper: (snapshot: DocumentSnapshot<DocumentData>) => T | undefined,
): Subscription<T> {
  const state = ref<LoadState<T>>(loading()) as Ref<LoadState<T>>

  const stop = onSnapshot(
    docRef,
    (snapshot) => {
      const value = mapper(snapshot)
      // Missing document is a fact, not a failure - callers distinguish absence from read errors.
      state.value = value === undefined ? absent() : loaded(value)
    },
    (error) => {
      state.value = failed(error.message)
    },
  )

  return { state, stop }
}

/** Subscribes to a Firestore query, exposing the mapped result as a reactive `LoadState`. */
export function subscribeCollection<T>(
  query: Query<DocumentData>,
  mapper: (snapshot: QuerySnapshot<DocumentData>) => T,
): Subscription<T> {
  const state = ref<LoadState<T>>(loading()) as Ref<LoadState<T>>

  const stop = onSnapshot(
    query,
    (snapshot) => {
      state.value = loaded(mapper(snapshot))
    },
    (error) => {
      state.value = failed(error.message)
    },
  )

  return { state, stop }
}
